import { FC, useEffect, useRef, useState } from "react";
import { Bar, BarChart, BarShapeProps, LabelList, Rectangle, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { QESWidget, QESWidgetContent, QESWidgetDescription, QESWidgetHeader, QESWidgetTitle } from "@app/shared/widget";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import ComplianceConnectService from "@app/compliance-connect/common/complianceConnectService";
import { AggregateExceptionReview, NetworkExceptionReview } from "@app/compliance-connect/regulator/complianceConnectTypes";
import { QesColors } from "@app/shared/qes-standards/colors";

interface ExceptionReviewStatusWidgetProps {
    networkIds: string[];
}

interface AggregateStat {
    label: string;
    subtitle?: string;
    testId: string;
    value: number;
}

interface BarDatum {
    fill: string;
    name: string;
    value: number;
}

const AggregateStatCell: FC<{ isLast: boolean; stat: AggregateStat }> = ({ isLast, stat }) => (
    <div
        className="d-flex flex-column align-items-center justify-content-center py-3"
        style={{
            borderRight: isLast ? undefined : `1px solid ${QesColors.QesPrimaryGray4}`,
            flex: 1,
        }}
    >
        <span className="label-s mb-1">{stat.label}</span>
        <span
            className="header-s mb-0"
            style={{ fontWeight: "bold", lineHeight: 1 }}
            data-testid={stat.testId}
        >
            {stat.value.toLocaleString()}
        </span>
        <span className="label-tiny mt-1" style={{ color: QesColors.QesPrimaryGray6, visibility: stat.subtitle?.trim() ? undefined : "hidden" }}>
            {stat.subtitle || " "}
        </span>
    </div>
);

const renderBarLabel = (props: { offset?: number; value?: string; x?: number; y?: number }) => {
    const { offset = 0, value, x = 0, y = 0 } = props;
    return (
        <text
            dominantBaseline="auto"
            fill={QesColors.QesPrimaryGray6}
            fontSize={11}
            fontWeight={600}
            textAnchor="start"
            x={x}
            y={y - offset}
        >
            {value}
        </text>
    );
};

const NetworkExceptionCard: FC<{ network: NetworkExceptionReview }> = ({ network }) => {
    const data: BarDatum[] = [
        { fill: QesColors.QesPrimaryGreen,      name: "Approved: " + network.approved.toLocaleString(), value: network.approved },
        { fill: QesColors.QesTertiaryYellow2,   name: "Pending: " + network.pending.toLocaleString(),   value: network.pending  },
        { fill: QesColors.QesPrimaryRed,        name: "Denied: " + network.denied.toLocaleString(),     value: network.denied   },
    ];

    return (
        <QESWidget className="flex-grow-1" data-testid={`compliance-connect__exception-review-network-card-${network.networkId}`} size="small">
            <QESWidgetHeader>
                <QESWidgetTitle>{network.networkName}</QESWidgetTitle>
                <QESWidgetDescription>
                    Total Exceptions: <strong>{network.total.toLocaleString()}</strong>
                </QESWidgetDescription>
            </QESWidgetHeader>
            <div className="mx-2" style={{ borderBottom: `1px solid ${QesColors.QesPrimaryGray4}` }} />
            <QESWidgetContent>
                <ResponsiveContainer height={150} width="100%">
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ bottom: 4, left: 0, right: 8, top: 24 }}
                    >
                        <XAxis hide type="number" />
                        <YAxis dataKey="name" hide type="category" />
                        <Bar
                            barSize={6}
                            dataKey="value"
                            radius={4}
                            shape={(shapeProps: BarShapeProps) => <Rectangle {...shapeProps} fill={shapeProps.payload.fill} />}
                        >
                            <LabelList content={renderBarLabel} dataKey="name" offset={12} position="top" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </QESWidgetContent>
        </QESWidget>
    );
};

const toRoundedPercent = (value: number, total: number): number =>
    total > 0 ? Math.round((value / total) * 100) : 0;

const ExceptionReviewStatusWidget: FC<ExceptionReviewStatusWidgetProps> = ({ networkIds }) => {
    const service = useRef(new ComplianceConnectService()).current;
    const [networks, setNetworks] = useState<NetworkExceptionReview[]>([]);
    const [aggregate, setAggregate] = useState<AggregateExceptionReview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(false);
        service.getExceptionReviewStatus(networkIds)
            .then(data => {
                setAggregate(data.aggregate);
                setNetworks(data.networks);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [networkIds, service]);

    const renderContent = () => {
        if (loading) return <LoadingSpinner />;
        if (error) return <QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert>;

        const stats: AggregateStat[] = aggregate ? [
            { label: "Total Exceptions", subtitle: `Across ${networkIds.length} plans`,                                       testId: "compliance-connect__exception-review-aggregate-total",    value: aggregate.total    },
            { label: "Total Approved",   subtitle: `${toRoundedPercent(aggregate.approved, aggregate.total)}% approval rate`, testId: "compliance-connect__exception-review-aggregate-approved", value: aggregate.approved },
            { label: "Pending Review",                                                                                        testId: "compliance-connect__exception-review-aggregate-pending",  value: aggregate.pending  },
            { label: "Denied",           subtitle: `${toRoundedPercent(aggregate.denied, aggregate.total)}% denial rate`,     testId: "compliance-connect__exception-review-aggregate-denied",   value: aggregate.denied   },
        ] : [];

        return (
            <>
                <div className="d-flex w-100">
                    {stats.map((stat, i) => (
                        <AggregateStatCell key={stat.testId} isLast={i === stats.length - 1} stat={stat} />
                    ))}
                </div>
                <div className="d-flex w-100 mt-3" style={{ gap: "0.75rem" }}>
                    {networks.map(n => (
                        <NetworkExceptionCard key={n.networkId} network={n} />
                    ))}
                </div>
            </>
        );
    };

    return (
    <div data-testid="compliance-connect__exception-review-status-widget">
        <QESWidget className="w-100">
            <QESWidgetHeader>
                <QESWidgetTitle>Exception Review Status</QESWidgetTitle>
            </QESWidgetHeader>
            <QESWidgetContent>
                    {renderContent()}
            </QESWidgetContent>
        </QESWidget>
    </div>
);
};

export default ExceptionReviewStatusWidget;
